import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { AppDialog, type AppDialogAction } from '../Common'
import { FormRenderer } from '../Forms/FormRenderer'
import {
  getEduQuiz,
  submitEduQuizAttempt,
  type GetEduQuizResult,
  type SubmitEduQuizAttemptResult,
} from '../../services/eduQuizService'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import { useNls } from '../../nls/nlsProvider'
import { formatCalendarDate } from '../../utils/eduFamilyFormat'
import type { FormDefinition, FormField } from '../../types/forms'
import './familyQuizDialog.css'

export interface FamilyQuizDialogProps {
  open: boolean
  workspaceId: string
  checklistId: string
  learnerId: string
  title?: string
  onClose: (attemptSubmitted?: boolean) => void
}

function buildQuizFormDefinition(
  formId: string,
  workspaceId: string,
  formMeta:
    | {
        title?: string
        description?: string
        logic?: FormDefinition['logic']
        theme?: FormDefinition['theme']
      }
    | undefined,
  fields: FormField[],
): FormDefinition {
  return {
    id: formId,
    workspaceId,
    title: formMeta?.title || '',
    titleSearch: (formMeta?.title || '').toLowerCase(),
    titlePrefixes: [],
    description: formMeta?.description || '',
    fields: fields || [],
    logic: { version: 1, rules: [] },
    theme: formMeta?.theme || {
      density: 'COMFORTABLE',
    },
    lifecycleStatus: 'ACTIVE',
    submissionCount: 0,
    deadline: null,
    isLocked: false,
    createdBy: '',
    createdAt: null,
    updatedAt: null,
  }
}

function formatQuizAnswer(val: unknown, field?: FormField): string {
  if (val == null || val === '') return '-'
  if (field && 'options' in field && Array.isArray(field.options)) {
    const opts = field.options
    if (Array.isArray(val)) {
      return val.map((v) => formatQuizAnswer(v, field)).join(', ')
    }
    const match = opts.find(
      (opt: { id: string; label: string; value: string }) =>
        opt.id === String(val) || opt.value === String(val),
    )
    if (match) return match.label
  }
  if (typeof val === 'boolean') {
    return val ? 'Yes' : 'No'
  }
  return String(val)
}

export const FamilyQuizDialog = ({
  open,
  workspaceId,
  checklistId,
  learnerId,
  title,
  onClose,
}: FamilyQuizDialogProps): ReactElement | null => {
  const { t, locale } = useNls()
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [quizData, setQuizData] = useState<GetEduQuizResult | null>(null)
  const [formDefinition, setFormDefinition] = useState<FormDefinition | null>(null)
  const [mode, setMode] = useState<'intro' | 'taking' | 'result' | 'closed'>('intro')
  const [lastResult, setLastResult] = useState<SubmitEduQuizAttemptResult | null>(null)
  const [attemptSubmitted, setAttemptSubmitted] = useState<boolean>(false)

  useEffect(() => {
    if (!open) return
    let active = true

    getEduQuiz({ workspaceId, checklistId, learnerId })
      .then((res) => {
        if (!active) return
        setQuizData(res)
        setFormDefinition(
          buildQuizFormDefinition(res.formId, workspaceId, res.form, res.fields),
        )
        if (res.closed) {
          setMode('closed')
        } else {
          setMode('intro')
        }
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (!active) return
        setError(mapEduServerError(err, t))
        setLoading(false)
      })

    return () => {
      active = false
    }
  }, [open, workspaceId, checklistId, learnerId, t])

  const handleClose = useCallback(() => {
    onClose(attemptSubmitted)
  }, [onClose, attemptSubmitted])

  const handleSubmitAnswers = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        const res = await submitEduQuizAttempt({
          workspaceId,
          checklistId,
          learnerId,
          answers: values,
        })
        setLastResult(res)
        setAttemptSubmitted(true)
        setQuizData((prev) => {
          if (!prev) return null
          const nextUsed = prev.attemptsUsed + 1
          const nextLeft = Math.max(0, prev.settings.maxAttempts - nextUsed)
          const isBetter =
            !prev.bestResult || res.score > prev.bestResult.score
          return {
            ...prev,
            attemptsUsed: nextUsed,
            attemptsLeft: nextLeft,
            bestResult: isBetter
              ? {
                  score: res.score,
                  maxScore: res.maxScore,
                  percent: res.percent,
                  passed: res.passed,
                  attemptNo: res.attemptNo,
                }
              : prev.bestResult,
          }
        })
        setMode('result')
      } catch (err) {
        const mappedMsg = mapEduServerError(err, t)
        throw new Error(mappedMsg, { cause: err })
      }
    },
    [workspaceId, checklistId, learnerId, t],
  )

  const handleTryAgain = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const fresh = await getEduQuiz({ workspaceId, checklistId, learnerId })
      setQuizData(fresh)
      setFormDefinition(
        buildQuizFormDefinition(fresh.formId, workspaceId, fresh.form, fresh.fields),
      )
      if (fresh.closed) {
        setMode('closed')
      } else if (fresh.attemptsLeft > 0) {
        setMode('taking')
      } else {
        setMode('intro')
      }
    } catch (err) {
      setError(mapEduServerError(err, t))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, checklistId, learnerId, t])

  if (!open) return null

  const dialogTitle = title || quizData?.form.title || t('NLSFamilyQuizDialogTitle')

  let actions: AppDialogAction[] = []
  if (mode === 'intro') {
    actions = [
      {
        label: t('NLSFamilyQuizClose'),
        onClick: handleClose,
        variant: 'secondary',
      },
      ...(quizData && quizData.attemptsLeft > 0 && !quizData.closed
        ? [
            {
              label: t('NLSFamilyQuizStart'),
              onClick: () => setMode('taking'),
              variant: 'primary' as const,
            },
          ]
        : []),
    ]
  } else if (mode === 'taking') {
    actions = [
      {
        label: t('NLSFamilyQuizClose'),
        onClick: handleClose,
        variant: 'secondary',
      },
    ]
  } else if (mode === 'result') {
    actions = [
      {
        label: t('NLSFamilyQuizClose'),
        onClick: handleClose,
        variant: 'secondary',
      },
      ...((quizData?.attemptsLeft ?? 0) > 0
        ? [
            {
              label: t('NLSFamilyQuizTryAgain'),
              onClick: handleTryAgain,
              variant: 'primary' as const,
            },
          ]
        : []),
    ]
  } else if (mode === 'closed') {
    actions = [
      {
        label: t('NLSFamilyQuizClose'),
        onClick: handleClose,
        variant: 'secondary',
      },
    ]
  }

  const renderContent = () => {
    if (loading) {
      return <div className="familyQuizDialog__loading">{t('NLSFamilyQuizLoading')}</div>
    }

    if (error) {
      return <div className="familyQuizDialog__error">{error}</div>
    }

    if (!quizData) return null

    if (mode === 'intro') {
      return (
        <div className="familyQuizDialog__intro">
          {quizData.form.description && (
            <p className="familyQuizDialog__description">{quizData.form.description}</p>
          )}

          <div className="familyQuizDialog__metaGrid">
            <div className="familyQuizDialog__metaRow">
              <span className="familyQuizDialog__metaLabel">{t('NLSFamilyQuizPassMark')}</span>
              <span className="familyQuizDialog__metaValue">
                {quizData.settings.passPercent}%
              </span>
            </div>

            <div className="familyQuizDialog__metaRow">
              <span className="familyQuizDialog__metaLabel">{t('NLSFamilyQuizAttempts')}</span>
              <span className="familyQuizDialog__metaValue">
                {quizData.attemptsUsed} / {quizData.settings.maxAttempts} (
                {quizData.attemptsLeft > 0
                  ? t('NLSFamilyQuizAttemptsLeft', { count: quizData.attemptsLeft })
                  : t('NLSFamilyQuizNoAttemptsLeft')}
                )
              </span>
            </div>

            {quizData.bestResult && (
              <div className="familyQuizDialog__metaRow">
                <span className="familyQuizDialog__metaLabel">
                  {t('NLSFamilyQuizBestResult')}
                </span>
                <span className="familyQuizDialog__metaValue">
                  {quizData.bestResult.score} / {quizData.bestResult.maxScore} (
                  {quizData.bestResult.percent}%)
                </span>
              </div>
            )}

            {quizData.settings.dueDate && (
              <div className="familyQuizDialog__metaRow">
                <span className="familyQuizDialog__metaLabel">{t('NLSFamilyQuizDueDate')}</span>
                <span className="familyQuizDialog__metaValue">
                  {formatCalendarDate(quizData.settings.dueDate, locale)}
                </span>
              </div>
            )}
          </div>
        </div>
      )
    }

    if (mode === 'taking' && formDefinition) {
      return (
        <div className="familyQuizDialog__taking">
          <FormRenderer
            definition={formDefinition}
            onSubmitAnswers={handleSubmitAnswers}
          />
        </div>
      )
    }

    if (mode === 'result' && lastResult) {
      return (
        <div className="familyQuizDialog__result">
          <div className="familyQuizDialog__resultScore">
            {lastResult.score} / {lastResult.maxScore} ({lastResult.percent}%)
          </div>

          <div
            className={`familyQuizDialog__resultStatus ${
              lastResult.passed ? 'isPassed' : 'isNotPassed'
            }`}
          >
            {lastResult.passed ? t('NLSFamilyQuizPassed') : t('NLSFamilyQuizNotPassed')}
          </div>

          <div className="familyQuizDialog__attemptsLeft">
            {(quizData?.attemptsLeft ?? 0) > 0
              ? t('NLSFamilyQuizAttemptsLeft', { count: quizData?.attemptsLeft ?? 0 })
              : t('NLSFamilyQuizNoAttemptsLeft')}
          </div>
        </div>
      )
    }

    if (mode === 'closed') {
      const scoredFieldIds = Object.keys(quizData.keys || {})
      const scoredQuestions = scoredFieldIds.map((id) => {
        const field: FormField = quizData.fields.find((f) => f.id === id) || {
          id,
          label: id,
          type: 'TEXT',
          required: false,
          order: 0,
          validation: {},
        }
        const key = quizData.keys![id]
        return { field, key }
      })

      return (
        <div className="familyQuizDialog__reviewList">
          {scoredQuestions.map(({ field, key }) => {
            const hasReview = quizData.review != null
            const learnerAnsRaw = quizData.review?.answers?.[field.id]
            const learnerAnsText = formatQuizAnswer(learnerAnsRaw, field)
            const correctAnsText = formatQuizAnswer(key.correct, field)
            const perField = quizData.review?.perField?.[field.id]
            const isCorrect = perField?.correct

            return (
              <div key={field.id} className="familyQuizDialog__reviewQuestion">
                <div className="familyQuizDialog__questionHeader">
                  <span className="familyQuizDialog__questionLabel">{field.label}</span>
                  {perField != null && (
                    <span
                      className={`familyQuizDialog__statusBadge ${
                        isCorrect ? 'isCorrect' : 'isIncorrect'
                      }`}
                    >
                      {isCorrect ? t('NLSFamilyQuizCorrect') : t('NLSFamilyQuizIncorrect')}
                    </span>
                  )}
                </div>

                {hasReview && (
                  <div className="familyQuizDialog__answerRow">
                    <span className="familyQuizDialog__answerLabel">
                      {t('NLSFamilyQuizYourAnswer')}:
                    </span>
                    <span className="familyQuizDialog__answerValue">{learnerAnsText}</span>
                  </div>
                )}

                <div className="familyQuizDialog__answerRow">
                  <span className="familyQuizDialog__answerLabel">
                    {t('NLSFamilyQuizCorrectAnswer')}:
                  </span>
                  <span className="familyQuizDialog__answerValue">{correctAnsText}</span>
                </div>

                {key.explanation && (
                  <div className="familyQuizDialog__explanation">
                    <span className="familyQuizDialog__explanationLabel">
                      {t('NLSFamilyQuizExplanation')}:
                    </span>{' '}
                    <span>{key.explanation}</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )
    }

    return null
  }

  return (
    <AppDialog
      open={open}
      title={dialogTitle}
      message={renderContent()}
      actions={actions}
      onDismiss={handleClose}
      size="md"
    />
  )
}
